import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from './userProfile.module.css';
import { API_URL } from '../Utils/api';
import { getNotif } from '../Utils/GetNotif';
import {
  FaArrowLeft, FaHeart, FaRegHeart, FaRegCommentDots,
  FaPaperPlane, FaRegBookmark, FaBookmark,
  FaUserPlus, FaUserCheck, FaEllipsisV,
  FaCalendarAlt, FaMapMarkerAlt,
  FaUser
} from 'react-icons/fa';
import { useUserProfile } from '../hooks/useUsers';


/* ─── Helpers ──────────────────────────────────────────────── */
const timeAgo = d => {
  if (!d) return '';
  const diff = Date.now() - new Date(d);
  const m = Math.floor(diff/60000), h = Math.floor(diff/3600000), dd = Math.floor(diff/86400000);
  if (m < 1)  return 'maintenant';
  if (m < 60) return `${m}min`;
  if (h < 24) return `${h}h`;
  if (dd < 7) return `${dd}j`;
  return new Date(d).toLocaleDateString('fr-FR',{day:'numeric',month:'short'});
};

const fmtJoined = d =>
  d ? new Date(d).toLocaleDateString('fr-FR',{month:'long',year:'numeric'}) : '';

const fmt = n => n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n);

/* ─── PostCard — même design que Posts.jsx ─────────────────── */
const PostCard = ({ post, userId, token, onNavigate }) => {
  const [liked,     setLiked]     = useState(post.postLike?.includes(userId) || false);
  const [likeCount, setLikeCount] = useState(post.postLike?.length || 0);
  const [saved,     setSaved]     = useState(false);

  const handleLike = async e => {
    e.stopPropagation();
    const prev = { liked, likeCount };
    setLiked(v => !v); setLikeCount(v => liked ? v - 1 : v + 1);
    try {
      await axios.put(`${API_URL}/post/like`,
        { postId: post._id, authorId: post.userId },
        { headers: { Authorization: `Bearer${token}` } });
    } catch { setLiked(prev.liked); setLikeCount(prev.likeCount); }
  };

  const handleShare = async e => {
    e.stopPropagation();
    const url = `${window.location.origin}/post/${post._id}`;
    if (navigator.share) try { await navigator.share({ url }); return; } catch {}
    navigator.clipboard.writeText(url);
  };

  return (
    <article
      className={`${styles.postCard} ${post.postPicture ? styles.postCardMedia : styles.postCardText}`}
      onClick={() => onNavigate(`/post/${post._id}`)}
    >
        {/* Post picture */}
        {post.postPicture && (
            <div className={styles.postMedia}>
                <img src={post.postPicture} alt="" loading="lazy"/>
            </div>
        )}

        {/* Text */}
        {post.postText && (
            <p className={styles.postCardText}>{post.postText}</p>
        )}
        {post.post?.type === 'question' && (
            <div>
                <h3 className={styles.postCardText}>{post.post?.question}</h3>
                <p className={styles.postCardText}>{post.post?.details}</p>
            </div>
            
        )}

        {post.post?.type === 'event' && (
            <div>
                <h3 className={styles.postCardText}>{post.post?.title}</h3>
                <p className={styles.postCardText}>{post.post?.description}</p>
            </div>
            
        )}

        {post.post?.postType === 'info' && (
            <div>
                <h3 className={styles.postCardText}>{post.post?.title}</h3>
                <div className={styles.postMedia}>
                    <img src={post.post?.imageUrl} alt="" loading="lazy"/>
                </div>
                
                <p className={styles.postCardText}>{post.post?.description}</p>
            </div>
            
        )}


        {/* Actions */}
        <div className={styles.postActions}>
            <div className={styles.postActionsLeft}>
            <button
                className={`${styles.postActionBtn} ${liked ? styles.postLiked : ''}`}
                onClick={handleLike}
            >
                {liked ? <FaHeart className={styles.iconLiked}/> : <FaRegHeart/>}
                {likeCount > 0 && <span>{likeCount}</span>}
            </button>

            <button
                className={styles.postActionBtn}
                onClick={e => { e.stopPropagation(); onNavigate(`/post/${post._id}`); }}
            >
                <FaRegCommentDots/>
                {post.postComment?.length > 0 && <span>{post.postComment.length}</span>}
            </button>

            <button className={styles.postActionBtn} onClick={handleShare}>
                <FaPaperPlane/>
            </button>
            </div>

            <div className={styles.postActionsRight}>
                <span className={styles.postTime}>{timeAgo(post.createdAt)}</span>
            <button
                className={`${styles.postActionBtn} ${saved ? styles.postSaved : ''}`}
                onClick={e => { e.stopPropagation(); setSaved(v => !v); }}
            >
                {saved ? <FaBookmark/> : <FaRegBookmark/>}
            </button>
            </div>
        </div>
    </article>
  );
};

/* ─── Main component ────────────────────────────────────────── */
const UserProfile = () => {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [activeTab, setActiveTab] = useState('posts'); // 'posts' | 'media'

  const token    = useMemo(() => localStorage.getItem('token'),  []);
  const myUserId = useMemo(() => localStorage.getItem('userId'), []);
  const isOwn    = id === myUserId;

  const { data: profile, isLoading: loading, isError } = useUserProfile(token, id);
  const error = isError ? 'Impossible de charger ce profil' : null;

  useEffect(() => {
    if (profile) {
      setFollowerCount(profile.followers?.length || 0);
      setFollowing(profile.followers?.some(f => f?.toString() === myUserId) || false);
    }
  }, [profile, myUserId]);

  /* follow */
  const handleFollow = useCallback(async () => {
    const prev = { following, followerCount };
    setFollowing(v => !v);
    setFollowerCount(v => following ? v - 1 : v + 1);
    try {
      await axios.put(`${API_URL}/follow`,
        { authorId: id },
        { headers: { Authorization: `Bearer${token}` } }
      );
      // Envoyer la notification via socket uniquement si c'est un nouveau follow (pas un désabonnement)
      if (!prev.following) {
        getNotif({ userId: myUserId, authorId: id, message: "a commencé à vous suivre", token });
      }
    } catch (err) {
      console.error('Erreur follow:', err);
      setFollowing(prev.following);
      setFollowerCount(prev.followerCount);
    }
  }, [id, token, following, followerCount]);
  const openchat = (id)=>{
    navigate(`/chat/${id}`)
  }
  /* filtered posts */
  const allPosts   = profile?.posts || [];
  const mediaPosts = allPosts.filter(p => p.postPicture || p.post?.imageUrl);

  /* ─── Loading skeleton ── */
  if (loading) return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}><FaArrowLeft/></button>
        <span className={styles.topbarTitle}>Profil</span>
        <div style={{width:36}}/>
      </div>
      <div className={styles.skeleton}>
        <div className={styles.skBanner}/>
        <div className={styles.skAvatarRow}>
          <div className={styles.skAvatar}/>
        </div>
        <div className={styles.skLines}>
          <div className={styles.skLine} style={{width:'45%'}}/>
          <div className={styles.skLine} style={{width:'30%'}}/>
          <div className={styles.skLine} style={{width:'60%'}}/>
        </div>
      </div>
    </div>
  );

  /* ─── Error ── */
  if (error || !profile) return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}><FaArrowLeft/></button>
        <span className={styles.topbarTitle}>Profil</span>
        <div style={{width:36}}/>
      </div>
      <div className={styles.errorState}>
        <span><FaUser/></span>
        <h3>Profil introuvable</h3>
        <p>Ce compte n'existe pas ou a été supprimé</p>
      </div>
    </div>
  );

  const displayedPosts = activeTab === 'media' ? mediaPosts : allPosts;

  return (
    <div className={styles.page}>

      {/* ── Topbar ── */}
      <div className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Retour">
          <FaArrowLeft/>
        </button>
        <div className={styles.topbarInfo}>
          <span className={styles.topbarTitle}>{profile.username}</span>
          <span className={styles.topbarSub}>{fmt(allPosts.length)} publications</span>
        </div>
        <button className={styles.moreBtn} aria-label="Options">
          <FaEllipsisV/>
        </button>
      </div>

      {/* ── Banner ── */}
      <div className={styles.banner}>
        <div className={styles.bannerGradient}/>
      </div>

      {/* ── Header section ── */}
      <div className={styles.profileHeader}>

        {/* Avatar + Follow button */}
        <div className={styles.avatarRow}>
          <div className={styles.avatarWrap}>
            {profile.userPP
              ? <img 
                src={profile.userPP || profile.pp || `https://ui-avatars.com/api/?name=${profile.username || 'U'}&background=f1f5f9&color=64748b`} 
                alt={profile.username} 
                className={styles.avatar}
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${profile.username || 'U'}&background=f1f5f9&color=64748b`;
                }} 
              />
              : (
                <div className={styles.avatarPlaceholder}>
                  {profile.username?.charAt(0).toUpperCase()}
                </div>
              )
            }
            {profile.socketId && <span className={styles.onlineDot}/>}
          </div>

            <div className={styles.buttons} >
                {!isOwn && (
                    <button
                    className={`${styles.followBtn} ${following ? styles.followBtnActive : ''}`}
                    onClick={handleFollow}
                    >
                    {following
                        ? <><FaUserCheck/> Suivi</>
                        : <><FaUserPlus/> Suivre</>
                    }
                    </button>
                )}
                {!isOwn && (
                    <button
                    className={styles.messageBtn} 
                    onClick={()=> openchat(profile.userId)}
                    >
                    Message
                    </button>
                )}
                {isOwn && (
                    <button className={styles.editBtn} onClick={() => navigate('/settings')}>
                    Modifier le profil
                    </button>
                )}
            </div>
        </div>

        {/* Name + username */}
        <div className={styles.nameBlock}>
          <h1 className={styles.displayName}>{profile.username}</h1>

          {profile.socketId
            ? <span className={styles.onlinePill}>● Actif maintenant</span>
            : <span className={styles.offlinePill}>Hors ligne</span>
          }
        </div>

        {/* Meta */}
        <div className={styles.metaRow}>
          <span className={styles.metaItem}>
            <FaCalendarAlt/> Inscrit en {fmtJoined(profile.createdAt)}
          </span>
        </div>

        {/* Stats */}
        <div className={styles.statsRow}>
          <div className={styles.stat} onClick={() => navigate(`/profile/${id}/following`)}>
            <span className={styles.statNum}>{fmt(profile.following?.length || 0)}</span>
            <span className={styles.statLabel}>abonnements</span>
          </div>
          <div className={styles.statDivider}/>
          <div className={styles.stat} onClick={() => navigate(`/profile/${id}/followers`)}>
            <span className={styles.statNum}>{fmt(followerCount)}</span>
            <span className={styles.statLabel}>abonnés</span>
          </div>
          <div className={styles.statDivider}/>
          <div className={styles.stat}>
            <span className={styles.statNum}>{fmt(allPosts.length)}</span>
            <span className={styles.statLabel}>publications</span>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'posts' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('posts')}
        >
          Publications
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'media' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('media')}
        >
          Médias
          {mediaPosts.length > 0 && (
            <span className={styles.tabCount}>{mediaPosts.length}</span>
          )}
        </button>
      </div>

      {/* ── Posts feed ── */}
      <div className={styles.feed}>
        {displayedPosts.length === 0 ? (
          <div className={styles.emptyState}>
            <span>{activeTab === 'media' ? '🖼️' : '📝'}</span>
            <p>{activeTab === 'media' ? 'Aucun média' : 'Aucune publication'}</p>
          </div>
        ) : (
          displayedPosts.map(post => (
            <PostCard
              key={post._id}
              post={post}
              userId={myUserId}
              token={token}
              onNavigate={navigate}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default UserProfile;